import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { GoogleButton } from "@/components/GoogleButton";
import { ArrowRight, Infinity as InfinityIcon } from "lucide-react";

const LOGIN_BG = "https://koioc.io/wp-content/uploads/2025/08/toronto-skyline-test-2.jpg";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      const to = location.state?.from?.pathname || "/dashboard";
      nav(to, { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white text-[#212121]">
      {/* Left: brand panel */}
      <div className="relative hidden lg:block overflow-hidden bg-[#F3F8F7]">
        <img src={LOGIN_BG} alt="Toronto skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2E26]/85 via-[#0B2E26]/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <InfinityIcon className="h-9 w-9 text-white" strokeWidth={2.5} />
            <span className="text-3xl font-bold font-heading tracking-wide text-white">KOIOC</span>
          </div>
          <div className="max-w-lg">
            <h1 className="text-4xl sm:text-5xl font-bold font-display text-white leading-tight">
              Empowering your buildings with data driven insights.
            </h1>
            <p className="mt-5 text-white/85 text-base leading-relaxed max-w-md">
              Optimize performance, enhance efficiency, and promote sustainability — your
              entire portfolio&apos;s time-series data in one operator console.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { k: "01", v: "Secure Access" },
                { k: "02", v: "Hourly Sync" },
                { k: "03", v: "NexGen Smart Buildings" },
              ].map((i) => (
                <div key={i.k} className="rounded-md border border-white/25 bg-white/10 backdrop-blur-sm p-4">
                  <div className="text-[11px] text-white/60 font-heading">{i.k}</div>
                  <div className="text-sm text-white font-heading font-semibold mt-1">{i.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center px-6 py-16 sm:px-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10 flex items-center gap-2">
            <InfinityIcon className="h-7 w-7 text-[#156F5B]" strokeWidth={2.5} />
            <span className="text-2xl font-bold font-heading text-[#156F5B]">KOIOC</span>
          </div>
          <div className="k-label">Operator Login</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold font-display text-[#212121]">Welcome back</h2>
          <p className="mt-2 text-[#4D4D4D] text-sm">Sign in to access your portfolio dashboards.</p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
            <div>
              <label className="k-sublabel">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="k-input mt-2"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="k-sublabel">Password</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="k-input mt-2"
                placeholder="••••••••"
              />
            </div>
            {err && (
              <div className="text-sm text-red-700 rounded-md border border-red-200 bg-red-50 px-4 py-2" data-testid="login-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={busy} data-testid="login-submit-button" className="k-btn group">
              {busy ? "Signing in…" : "Sign in"}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5" style={{ transition: "transform .2s ease" }} />
            </button>
            <div className="text-xs text-[#6B7280] flex items-center justify-between">
              <span>
                No account?{" "}
                <Link to="/register" className="text-[#156F5B] font-semibold hover:underline" data-testid="link-register">
                  Request access
                </Link>
              </span>
              <Link to="/forgot-password" className="text-[#6B7280] hover:text-[#156F5B] hover:underline" data-testid="link-forgot-password">
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-8 flex items-center gap-4">
            <div className="h-px flex-1 bg-[#E5E7EB]" />
            <span className="k-sublabel">or</span>
            <div className="h-px flex-1 bg-[#E5E7EB]" />
          </div>
          <div className="mt-6">
            <GoogleButton label="Sign in with Google" />
            <p className="mt-3 text-xs text-[#9CA3AF]">
              New Google accounts start without portfolio access until an administrator grants it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
